import { Head, Link, router } from '@inertiajs/react'
import { useState } from 'react'
import { User, Organization, OrganizationSettings } from '../../types'
import UserAvatar from '../../components/shared/UserAvatar'
import Layout from '../../components/Layout'
import { sortPeople } from '../../utils/peopleSort'

interface Props {
  organization: Organization
  settings: OrganizationSettings
  hour_options: number[]
  users: User[]
}

function formatHours(hours: number): string {
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'}`
  const days = hours / 24
  if (Number.isInteger(days)) return `${days} day${days === 1 ? '' : 's'}`
  return `${hours} hours`
}

export default function AdminSettings({ organization, settings, hour_options, users }: Props) {
  const [form, setForm] = useState({
    auto_complete_enabled: settings.auto_complete_enabled,
    auto_complete_after_hours: settings.auto_complete_after_hours,
    send_recap_email: settings.send_recap_email,
    include_ratings_in_email: settings.include_ratings_in_email,
    include_section_durations: settings.include_section_durations
  })
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    setSaved(false)
    router.put('/admin/settings', {
      organization_setting: form
    }, {
      onFinish: () => {
        setSaving(false)
        setSaved(true)
        setTimeout(() => setSaved(false), 3000)
      }
    })
  }

  return (
    <Layout>
      <Head title="Admin Settings - Pinnacle" />

      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-text-primary font-heading">{organization.name}</h1>
          <p className="text-lg text-text-secondary mt-1">Manage your organization's meeting settings and automation rules.</p>
        </div>

        <Link href="/admin/ninety_imports" className="group flex items-center justify-between gap-4 bg-[#FCFAF7] border border-stone/15 rounded-xl p-5 mb-6 hover:border-pinnacle-red/30 transition-colors">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-pinnacle-red/10 text-pinnacle-red flex items-center justify-center font-heading font-bold">N</div>
            <div>
              <h2 className="font-heading text-lg font-semibold text-text-primary">Import from Ninety</h2>
              <p className="text-sm text-text-secondary mt-0.5">Stage exports, map people and teams, then validate one safe import.</p>
            </div>
          </div>
          <span className="shrink-0 text-sm font-semibold text-pinnacle-red">Open import <span aria-hidden="true">→</span></span>
        </Link>

        <Link href="/admin/strety_imports" className="flex items-center justify-between gap-4 border border-stone/15 rounded-xl p-5 mb-6 hover:border-pinnacle-red/30">
          <div>
            <h2 className="font-heading text-lg font-semibold text-text-primary">Import from Strety</h2>
            <p className="text-sm text-text-secondary mt-1">Native scoreboard CSVs and human-reviewed staging workbooks. Review every change before importing.</p>
          </div>
          <span className="text-sm font-semibold text-pinnacle-red">Open import →</span>
        </Link>

        <form onSubmit={handleSubmit}>
          <div className="bg-white rounded-xl shadow-card p-6 mb-6">
            <div className="flex items-center gap-3 mb-6 pb-4 border-b border-stone/10">
              <div className="w-10 h-10 bg-charcoal/5 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h2 className="text-xl font-semibold text-text-primary font-heading">Auto-Complete Meeting Agenda</h2>
                <p className="text-base text-text-secondary">Automatically complete meetings that have been running too long.</p>
              </div>
            </div>

            <div className="space-y-6">
              <label className="flex items-start gap-4 cursor-pointer group">
                <div className="relative mt-0.5">
                  <input
                    type="checkbox"
                    checked={form.auto_complete_enabled}
                    onChange={(e) => setForm(prev => ({ ...prev, auto_complete_enabled: e.target.checked }))}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-stone/30 rounded-full peer-checked:bg-pinnacle-red transition-colors" />
                  <div className="absolute left-0.5 top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform peer-checked:translate-x-5" />
                </div>
                <div>
                  <p className="text-base font-medium text-text-primary group-hover:text-text-primary">
                    Enable auto-complete
                  </p>
                  <p className="text-base text-text-secondary mt-0.5">
                    When enabled, meetings that exceed the configured time limit will be automatically completed.
                  </p>
                </div>
              </label>

              {form.auto_complete_enabled && (
                <div className="ml-15 pl-4 border-l-2 border-stone/20 space-y-6">
                  <div>
                    <label className="block text-base font-medium text-text-primary mb-2">
                      Auto-complete after
                    </label>
                    <select
                      value={form.auto_complete_after_hours}
                      onChange={(e) => setForm(prev => ({ ...prev, auto_complete_after_hours: parseInt(e.target.value) }))}
                      className="w-64 px-3 py-2 border border-stone/30 rounded-lg text-base text-text-primary focus:outline-none focus:ring-2 focus:ring-pinnacle-red/20 focus:border-pinnacle-red bg-white"
                    >
                      {hour_options.map(h => (
                        <option key={h} value={h}>{formatHours(h)}</option>
                      ))}
                    </select>
                    <p className="text-base text-text-secondary mt-1.5">
                      Meetings running longer than this duration from their start time will be automatically completed.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-card p-6 mb-6">
            <div className="flex items-center gap-3 mb-6 pb-4 border-b border-stone/10">
              <div className="w-10 h-10 bg-charcoal/5 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <h2 className="text-xl font-semibold text-text-primary font-heading">Post-Meeting Recap Email</h2>
                <p className="text-base text-text-secondary">Send a summary email after a meeting is auto-completed.</p>
              </div>
            </div>

            <div className="space-y-6">
              <label className="flex items-start gap-4 cursor-pointer group">
                <div className="relative mt-0.5">
                  <input
                    type="checkbox"
                    checked={form.send_recap_email}
                    onChange={(e) => setForm(prev => ({ ...prev, send_recap_email: e.target.checked }))}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-stone/30 rounded-full peer-checked:bg-pinnacle-red transition-colors" />
                  <div className="absolute left-0.5 top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform peer-checked:translate-x-5" />
                </div>
                <div>
                  <p className="text-base font-medium text-text-primary group-hover:text-text-primary">
                    Send recap email on auto-complete
                  </p>
                  <p className="text-base text-text-secondary mt-0.5">
                    When a meeting is auto-completed, send a recap email with the meeting summary and action items.
                  </p>
                </div>
              </label>

              {form.send_recap_email && (
                <div className="ml-15 pl-4 border-l-2 border-stone/20">
                  <label className="block text-base font-medium text-text-primary mb-3">
                    Send recap to
                  </label>
                  <p className="text-base text-text-secondary">
                    Everyone on the meeting roster, including absent attendees. Deactivated accounts and invalid email addresses are excluded.
                  </p>

                  <div className="mt-6 pt-4 border-t border-stone/10 space-y-3">
                    <p className="text-sm font-medium text-text-primary">What to include in the email</p>
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.include_ratings_in_email}
                        onChange={(e) => setForm(prev => ({ ...prev, include_ratings_in_email: e.target.checked }))}
                        className="w-4 h-4 mt-1"
                      />
                      <div>
                        <p className="text-base text-text-primary">Include attendee ratings</p>
                        <p className="text-sm text-text-secondary">Show each attendee's meeting rating and any public comments.</p>
                      </div>
                    </label>
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.include_section_durations}
                        onChange={(e) => setForm(prev => ({ ...prev, include_section_durations: e.target.checked }))}
                        className="w-4 h-4 mt-1"
                      />
                      <div>
                        <p className="text-base text-text-primary">Include per-section durations</p>
                        <p className="text-sm text-text-secondary">Add a breakdown of how long each agenda section took.</p>
                      </div>
                    </label>
                    <p className="text-xs text-text-muted pt-1">
                      Note: SendGrid configuration (API key, sender, template) is now managed system-wide by the platform Super Admin.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-card p-6 mb-6">
            <div className="flex items-center gap-3 mb-6 pb-4 border-b border-stone/10">
              <div className="w-10 h-10 bg-charcoal/5 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <h2 className="text-xl font-semibold text-text-primary font-heading">Scoreboard Week Labels</h2>
                <p className="text-base text-text-secondary">Week-label display is now managed per team.</p>
              </div>
            </div>

            <p className="text-base text-text-secondary">
              Each team now chooses whether its scoreboard columns show the week's start or end date.
              Change it directly on a team's Scoreboard page or from a live meeting's scoreboard section.
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-card p-6 mb-6">
            <div className="flex items-center gap-3 mb-6 pb-4 border-b border-stone/10">
              <div className="w-10 h-10 bg-charcoal/5 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <div>
                <h2 className="text-xl font-semibold text-text-primary font-heading">Executive Leadership</h2>
                <p className="text-base text-text-secondary">Tag team members as Executive Leadership. They will appear prominently on meeting template cards.</p>
              </div>
            </div>

            <div className="space-y-3">
              {sortPeople(users).map(user => (
                <div key={user.id} className="flex items-center justify-between py-3 px-4 rounded-lg hover:bg-[#F5F5F5]/30 transition-colors">
                  <div className="flex items-center gap-3">
                    <UserAvatar user={user} size="md" />
                    <div>
                      <p className="text-base font-medium text-text-primary">{user.full_name}</p>
                      <p className="text-sm text-text-secondary">{user.email} · {user.role}</p>
                    </div>
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <span className={`text-sm font-medium ${user.is_executive_leadership ? 'text-pinnacle-red' : 'text-text-muted'}`}>
                      {user.is_executive_leadership ? 'EL' : ''}
                    </span>
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={user.is_executive_leadership}
                        onChange={() => {
                          router.put(`/admin/users/${user.id}`, {
                            user: { is_executive_leadership: !user.is_executive_leadership }
                          }, { preserveScroll: true })
                        }}
                        className="sr-only peer"
                      />
                      <div className="w-9 h-5 bg-stone/30 rounded-full peer-checked:bg-pinnacle-red transition-colors" />
                      <div className="absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform peer-checked:translate-x-4" />
                    </div>
                  </label>
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between">
            <Link href="/" className="text-base text-text-secondary hover:text-text-primary">
              Back to Dashboard
            </Link>
            <div className="flex items-center gap-3">
              {saved && (
                <span className="text-base text-[#15532E] font-medium flex items-center gap-1.5">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Settings saved
                </span>
              )}
              <button
                type="submit"
                disabled={saving}
                className="bg-pinnacle-red hover:bg-pinnacle-red/90 disabled:opacity-50 text-white font-semibold py-3 px-6 rounded-lg transition-colors font-heading text-base"
              >
                {saving ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </Layout>
  )
}
